import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { forkJoin } from 'rxjs';
import { HiveMessageService, HiveMessage, HiveTemplate, HiveChannel } from '../services/hivemessage.service';

@Component({
  selector: 'lib-hivemessage-modal',
  templateUrl: './hivemessage-modal.component.html',
  styleUrls: ['./hivemessage-modal.component.scss']
})
export class HiveMessageModalComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer: ElementRef;

  @Input() phoneNumber: string;
  @Input() moduleType: string;
  @Input() moduleId: string;
  @Input() visible: boolean = false;
  @Output() close = new EventEmitter<void>();

  messages: HiveMessage[] = [];
  newMessage: string = '';
  loading: boolean = false;
  sending: boolean = false;
  templates: HiveTemplate[] = [];
  channels: HiveChannel[] = [];
  
  // Initial state is null to force selection
  selectedChannel: HiveChannel | null = null;
  contactName: string = '';
  avatarGender: 'male' | 'female' | 'neutral' = 'neutral';
  
  showTemplatePopup: boolean = false;
  isTemplateLocked: boolean = false;
  selectedTemplate: HiveTemplate | null = null;
  templateParameters: any[] = [];
  
  replyToMessage: HiveMessage | null = null;

  uploadingFile: boolean = false;
  attachmentUrl: string = '';
  attachmentFilename: string = '';
  selectedMediaType: 'image' | 'document' | 'video' | 'audio' = 'image';
  selectedFile: File | null = null;

  // Smart Toast Logic
  showInfoToast: boolean = false; // Warning Toast
  showSuccessToast: boolean = false; // Success Toast
  
  private infoToastTimer: any;
  private successToastTimer: any;
  
  isWithin24HourWindow: boolean = true;

  // 🔥 FIX: Guard to prevent double initialization
  private initialized: boolean = false;

  constructor(
    private hivemessageService: HiveMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Only init from ngOnInit if visible is already true on first render
    if (this.visible && !this.initialized) {
      this.initialized = true;
      this.initData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (changes['visible'].currentValue === true && !this.initialized) {
        // First time opening — init
        this.initialized = true;
        this.initData();
      } else if (changes['visible'].currentValue === true && this.initialized) {
        // Re-opening — just refresh messages
        this.loadMessages();
      } else if (changes['visible'].currentValue === false) {
        // Modal closed — reset init flag so next open re-inits fresh
        this.initialized = false;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.infoToastTimer) clearTimeout(this.infoToastTimer);
    if (this.successToastTimer) clearTimeout(this.successToastTimer);
  }

  initData(): void {
    this.loadMessages();
    this.loadChannels(); 
    this.loadContactDetails();
  }

  // 🔥 UPDATED: Show warning toast for exactly 6 seconds if outside 24h window
  onUserTyping() {
    if (this.isTemplateLocked) return;
    if (!this.selectedChannel) return; // Don't show warnings if channel isn't selected yet

    // If within 24 hours, no warning needed
    if (this.isWithin24HourWindow) {
      this.showInfoToast = false;
      return;
    }

    // If typing outside 24h window
    if (this.newMessage && this.newMessage.trim().length > 0) {
      this.showInfoToast = true;

      if (this.infoToastTimer) {
        clearTimeout(this.infoToastTimer);
      }

      // Hide after 6 seconds
      this.infoToastTimer = setTimeout(() => {
        this.showInfoToast = false;
        this.cdr.detectChanges();
      }, 6000);
      
    } else {
      this.showInfoToast = false;
    }
  }

  onEnterKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (!keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  private check24HourWindow(): void {
    const inboundMessages = this.messages.filter(m => m.direction === 'inbound');

    if (inboundMessages.length === 0) {
      this.isWithin24HourWindow = false;
      return;
    }

    const lastInbound = inboundMessages[inboundMessages.length - 1];
    const lastInboundTime = new Date(lastInbound.timestamp).getTime();
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;

    this.isWithin24HourWindow = (now - lastInboundTime) <= twentyFourHours;
  }

  loadChannels(): void {
    this.hivemessageService.getChannels().subscribe({
      next: (channels) => {
        this.channels = channels;
        this.selectedChannel = null;
        this.templates = []; 
        this.cdr.detectChanges();
      },
      error: () => {
        this.channels = [];
        this.cdr.detectChanges();
      }
    });
  }

  loadMessages(): void {
    if (!this.phoneNumber) return;
    this.loading = true;
    this.cdr.detectChanges();

    this.hivemessageService.getConversationHistory(this.phoneNumber).subscribe({
      next: (messages) => {
        this.messages = messages || [];
        this.loading = false;
        this.check24HourWindow();
        this.cdr.detectChanges();
        // 🔥 FIX: Scroll after DOM has rendered the messages
        setTimeout(() => this.scrollToBottom(), 50);
      },
      error: () => {
        this.messages = [];
        this.loading = false;
        this.isWithin24HourWindow = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadContactDetails(): void {
    if (!this.moduleType || !this.moduleId) {
      this.contactName = this.phoneNumber || '';
      this.avatarGender = 'neutral';
      return;
    }

    this.hivemessageService.getRecordDetails(this.moduleType, this.moduleId).subscribe({
      next: (details) => {
        this.contactName = this.resolveContactName(details);
        this.avatarGender = this.resolveAvatarGender(details);
        this.cdr.detectChanges();
      },
      error: () => {
        this.contactName = this.phoneNumber || '';
        this.avatarGender = 'neutral';
        this.cdr.detectChanges();
      }
    });
  }

  private resolveContactName(details: any): string {
    const salutation = (details?.salutation || details?.salutation_c || details?.title || '').toString().trim();
    const firstName = (details?.first_name || details?.firstname || details?.firstName || '').toString().trim();
    const lastName = (details?.last_name || details?.lastname || details?.lastName || '').toString().trim();
    const name = (details?.name || '').toString().trim();

    let fullName = name || `${firstName} ${lastName}`.trim();
    if (!fullName) {
      fullName = this.phoneNumber || '';
    }

    if (salutation && !fullName.toLowerCase().startsWith(salutation.toLowerCase())) {
      fullName = `${salutation} ${fullName}`.trim();
    }

    return fullName;
  }

  private resolveAvatarGender(details: any): 'male' | 'female' | 'neutral' {
    const salutation = (details?.salutation || details?.salutation_c || '').toString().trim().toLowerCase();
    const gender = (details?.gender || details?.gender_c || '').toString().trim().toLowerCase();

    if (gender.includes('female') || salutation.startsWith('ms') || salutation.startsWith('mrs') || salutation.startsWith('miss')) {
      return 'female';
    }
    if (gender.includes('male') || salutation.startsWith('mr')) {
      return 'male';
    }
    return 'neutral';
  }

  loadTemplates(): void {
    if (!this.selectedChannel) return; 
    
    const channelId = this.selectedChannel.channel_id;
    this.hivemessageService.getTemplates(channelId).subscribe({
      next: (templates) => {
        this.templates = templates || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.templates = [];
        this.cdr.detectChanges();
      }
    });
  }

  toggleTemplatePopup() {
    if (!this.selectedChannel) {
        alert("Please select a WhatsApp Channel first.");
        return;
    }
    this.showTemplatePopup = !this.showTemplatePopup;
    this.cdr.detectChanges();
  }

  replyToThisMessage(message: HiveMessage) {
    this.replyToMessage = message;
    this.cdr.detectChanges();
  }

  cancelReply() {
    this.replyToMessage = null;
    this.cdr.detectChanges();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.uploadingFile = true;
      this.attachmentUrl = ''; 

      if (file.type.includes('image')) this.selectedMediaType = 'image';
      else if (file.type.includes('video')) this.selectedMediaType = 'video';
      else this.selectedMediaType = 'document';

      this.hivemessageService.uploadMedia(file).subscribe({
        next: (response) => {
          this.uploadingFile = false;
          if (response.success) {
            this.attachmentUrl = response.url;
            this.attachmentFilename = response.filename;
          } else {
            alert('Upload Failed: ' + response.message);
            this.clearAttachment();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.uploadingFile = false;
          alert('Server Error during upload');
          this.clearAttachment();
          this.cdr.detectChanges();
        }
      });
    }
  }

  clearAttachment() {
    this.attachmentUrl = '';
    this.attachmentFilename = '';
    this.selectedFile = null;
    this.cdr.detectChanges();
  }

  onChannelChange() {
    this.templates = []; 
    this.selectedTemplate = null;
    this.templateParameters = [];
    this.isTemplateLocked = false;
    this.newMessage = '';
    this.showInfoToast = false; // Hide warning on channel change

    if (this.selectedChannel) {
      this.loadTemplates();
    }
    this.cdr.detectChanges();
  }

  selectTemplate(template: HiveTemplate) {
    if (!template || !template.content) return;

    this.showTemplatePopup = false;
    this.isTemplateLocked = true;
    this.selectedTemplate = template;
    this.newMessage = template.content;
    this.templateParameters = []; 
    this.clearAttachment(); 
    this.showInfoToast = false;
    this.cdr.detectChanges();

    if (this.moduleType && this.moduleId) {
      forkJoin({
        mappings: this.hivemessageService.getTemplateMappings(template.template_id, this.moduleType),
        recordDetails: this.hivemessageService.getRecordDetails(this.moduleType, this.moduleId)
      }).subscribe({
        next: ({ mappings, recordDetails }) => {
          let processedText = template.content || '';
          const matches = processedText.match(/\{\{(.+?)\}\}/g);
          const extractedParams: any[] = [];

          if (matches) {
            matches.forEach(match => {
              const paramName = match.replace('{{', '').replace('}}', '');
              let value = '';
              const mapping = mappings.find(m => m.parameter_name === paramName);
              if (mapping) value = recordDetails[mapping.mapped_field] || mapping.default_value || '';
              else if (recordDetails[paramName]) value = recordDetails[paramName];

              if (value) {
                processedText = processedText.replace(match, value);
                extractedParams.push({ type: 'text', value: value });
              }
            });
          }
          this.newMessage = processedText;
          this.templateParameters = extractedParams;
          this.cdr.detectChanges();
        }
      });
    }
  }

  clearTemplate(): void {
    this.newMessage = '';
    this.isTemplateLocked = false;
    this.selectedTemplate = null;
    this.templateParameters = [];
    this.showInfoToast = false;
    this.cdr.detectChanges();
  }

  sendMessage(): void {
    const text = this.newMessage.trim();
    if (!this.selectedChannel) {
        alert("Please select a channel first.");
        return;
    }
    const channelId = this.selectedChannel.channel_id;

    if (!text && !this.attachmentUrl && !this.selectedTemplate) return;

    // Reset warnings and start sending
    this.showInfoToast = false;
    if (this.infoToastTimer) clearTimeout(this.infoToastTimer);

    this.sending = true; // 🔥 Starts the rolling spinner in UI
    let apiCall;

    if (this.selectedTemplate) {
      apiCall = this.hivemessageService.sendTemplateMessage(
        this.phoneNumber,
        this.selectedTemplate.template_id,
        channelId,
        this.templateParameters.length > 0 ? this.templateParameters : undefined,
        this.moduleType,
        this.moduleId,
        this.newMessage
      );
    } else if (this.attachmentUrl) {
      apiCall = this.hivemessageService.sendMediaMessage(
        this.phoneNumber,
        this.selectedMediaType,
        this.attachmentUrl,
        channelId,
        text,
        this.attachmentFilename,
        this.moduleType,
        this.moduleId,
        this.replyToMessage?.message_id
      );
    } else {
      apiCall = this.hivemessageService.sendMessage(
        this.phoneNumber,
        text,
        channelId,
        this.moduleType,
        this.moduleId,
        this.replyToMessage?.message_id
      );
    }

    apiCall.subscribe({
      next: () => {
        this.messages.push({
          phoneNumber: this.phoneNumber,
          message: text,
          direction: 'outbound',
          status: 'sent',
          timestamp: new Date(),
          message_type: this.attachmentUrl ? this.selectedMediaType : 'text',
          media_url: this.attachmentUrl || ''
        });

        // Clear input state
        this.newMessage = '';
        this.isTemplateLocked = false;
        this.selectedTemplate = null;
        this.templateParameters = [];
        this.replyToMessage = null;
        this.clearAttachment(); 
        
        this.sending = false; // 🔥 Stop Spinner

        // 🔥 Show Success Toast
        this.showSuccessToast = true;
        if (this.successToastTimer) clearTimeout(this.successToastTimer);
        this.successToastTimer = setTimeout(() => {
          this.showSuccessToast = false;
          this.cdr.detectChanges();
        }, 3000);

        this.cdr.detectChanges();
        // 🔥 FIX: Scroll after DOM updates
        setTimeout(() => this.scrollToBottom(), 50);
      },
      error: () => {
        this.sending = false;
        this.cdr.detectChanges();
        // Optional: Show error toast here
      }
    });
  }

  closeModal(): void {
    this.visible = false;
    this.close.emit();
  }

  // ═══════════════ Date Grouping Helpers ═══════════════

  shouldShowDateSeparator(index: number): boolean {
    if (index === 0) return true;
    const currentDate = new Date(this.messages[index].timestamp);
    const prevDate = new Date(this.messages[index - 1].timestamp);
    return currentDate.toDateString() !== prevDate.toDateString();
  }

  getDateLabel(timestamp: Date): string {
    const msgDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (msgDate.toDateString() === today.toDateString()) return 'Today';
    if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';

    return msgDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: msgDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }

  isFirstInGroup(index: number): boolean {
    if (index === 0) return true;
    return this.messages[index].direction !== this.messages[index - 1].direction
        || this.shouldShowDateSeparator(index);
  }

  scrollToBottom(): void {
    try {
      if (this.messagesContainer?.nativeElement) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) { }
  }
}